import { ArrowPathIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { RichTextEditor } from 'components/RichTextEditor'
import { agreementTableExtensions } from 'components/RichTextEditor/tableExtensions'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { downloadFile, getAdminConfig, setAdminConfig } from 'services'
import Api from 'services/api'
import { Button, ButtonGroup } from 'stories/components'
import { confirm, generateErrorHtml } from 'utils'

import { validatePlaceholders } from '../LoanTermSheet/logic'

const TEMPLATE_OPTIONS = {
  entity: 'Entity',
  nonEntity: 'Individual',
}

const toEditorHtml = (html: string) => {
  if (!html || html.includes('data-placeholder')) return html || ''
  return html.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_match, key) => {
    const name = String(key).trim()
    return `<span data-placeholder="${name}" class="rte-placeholder">{{${name}}}</span>`
  })
}

export const EscrowHoldbackAgreementTemplate = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [action, setAction] = useState('')
  const [changed, setChanged] = useState(false)
  const [template, setTemplate] = useState('entity')
  const [data, setData] = useState<Record<string, string>>({ entity: '', nonEntity: '' })
  const [saved, setSaved] = useState<Record<string, string>>({ entity: '', nonEntity: '' })
  const [defaults, setDefaults] = useState<Record<string, string>>({ entity: '', nonEntity: '' })
  const [placeholders, setPlaceholders] = useState<string[]>([])
  const [error, setError] = useState('')

  const initData = async () => {
    setIsLoading(true)
    const res = await getAdminConfig('escrowHoldbackAgreementTemplate')
    const next = {
      entity: toEditorHtml(res?.entity || ''),
      nonEntity: toEditorHtml(res?.nonEntity || ''),
    }
    setData(next)
    setSaved(next)
    setDefaults({
      entity: toEditorHtml(res?.defaults?.entity || ''),
      nonEntity: toEditorHtml(res?.defaults?.nonEntity || ''),
    })
    setPlaceholders(res?.placeholders || [])
    setIsLoading(false)
    setChanged(false)
    setError('')
  }

  useEffect(() => {
    initData()
  }, [])

  const invalidPlaceholders = (html: string) => {
    const { hasError, errorPlaceholders } = validatePlaceholders(html, placeholders, true, false)
    return hasError ? errorPlaceholders : []
  }

  const onSubmit = async () => {
    const problems = invalidPlaceholders(data[template] || '')
    if (problems.length) {
      setError('Invalid placeholders exist')
      const html = generateErrorHtml(
        'Following Placeholders are not valid. Please check them again.',
        problems.map((item: string) => `{{${item}}}`),
      )
      await confirm(html, { titleYes: false, titleNo: 'Close' })
      return false
    }
    setIsLoading(true)
    setAction('save')
    await setAdminConfig('escrowHoldbackAgreementTemplate', { template, content: data[template] })
    setSaved((prev) => ({ ...prev, [template]: data[template] }))
    toast('Successfully submitted', { type: 'success' })
    setIsLoading(false)
    setAction('')
    setChanged(false)
    setError('')
    return true
  }

  const onChangeTemplate = async (value: string) => {
    if (value === template) return
    if (changed) {
      const keep = await confirm('Do you want to save the changes before switching to the other template?')
      if (keep) {
        const didSave = await onSubmit()
        if (!didSave) return
      } else {
        setData(cloneDeep(saved))
        setChanged(false)
      }
    }
    setTemplate(value)
    setError('')
  }

  const onReset = async () => {
    const result = await confirm('Replace this template with the original agreement wording?')
    if (!result) return
    const temp = cloneDeep(data)
    temp[template] = defaults[template] || ''
    setData(temp)
    setChanged(true)
    setError('')
  }

  const onPreview = async () => {
    const problems = invalidPlaceholders(data[template] || '')
    if (problems.length) {
      setError('Invalid placeholders exist')
      return
    }
    setAction('preview')
    const res: { name?: string; file?: string } = await Api.post(
      'admin/config/escrowHoldbackAgreementTemplate/preview',
      { content: data[template] },
    )
    if (res?.file) {
      const bytes = Uint8Array.from(atob(res.file), (char) => char.charCodeAt(0))
      downloadFile(res.name || 'Escrow Holdback Agreement Preview.pdf', new Blob([bytes], { type: 'application/pdf' }))
    }
    setAction('')
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <h2 className="text-2xl font-bold flex items-center mb-2">Escrow Holdback Agreement Template</h2>
      <p className="text-sm text-gray-600 mb-4">
        Entity loans use the Entity template. Loans without an entity title use the Individual template. Generated
        agreements use this wording, and the text here is the current agreement until it is changed.
      </p>
      <div className="mb-6">
        <p className="ml-3 text-lg font-medium mb-2">- Placeholders</p>
        <div className="p-3 border rounded-md">
          <div className="flex gap-x-1 gap-y-3 flex-wrap justify-between">
            {[...placeholders].sort().map((item) => (
              <pre key={item} className="w-[240px]">
                {item}
              </pre>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mb-4">
        <ButtonGroup title={TEMPLATE_OPTIONS} value={template} onChange={(value) => onChangeTemplate(value)} />
        <div className="flex items-center gap-4">
          <Button link onClick={onReset}>
            <div className="flex gap-2 items-center">
              <ArrowPathIcon className="w-4 h-4" />
              Reset to original
            </div>
          </Button>
          <Button outline onClick={onPreview} loading={action === 'preview'}>
            Download preview
          </Button>
        </div>
      </div>
      <RichTextEditor
        value={data[template] || ''}
        onChange={(html) => {
          const temp = cloneDeep(data)
          temp[template] = html
          setData(temp)
          setChanged(true)
          setError('')
        }}
        placeholders={placeholders}
        extensions={agreementTableExtensions}
        error={error}
        minHeight={420}
        ariaLabel="Escrow holdback agreement"
      />
      <div className="flex justify-center mt-4">
        <Button onClick={onSubmit} loading={action === 'save'}>
          Save
        </Button>
      </div>
    </div>
  )
}
